import React, { memo, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Circle, MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import styles from './GeofenceMap.module.scss';

/* ────────────────────────────────────────────────────────────
 * GeofenceMap — read-only view of one point and, optionally,
 * the circular geofence around it.
 *
 *   <GeofenceMap
 *     lat={location.Lat}
 *     lng={location.Long}
 *     radius={location.Radius}          // metres; 0 hides the circle
 *     markerIconUrl={locationPin}
 *   />
 *
 * The circle is drawn in metres, so it scales with the map:
 * zooming out shrinks it on screen but never changes what it
 * means. The view auto-fits the geofence, so the caller does not
 * have to pick a zoom that happens to suit the radius.
 *
 * Unlike MapPicker this takes no input and emits no change — use
 * MapPicker when the user is choosing a point, this when they are
 * only looking at one.
 * ──────────────────────────────────────────────────────────── */

const DEFAULT_CIRCLE_STYLE = {
  color: '#2563eb',
  weight: 2,
  fillColor: '#3b82f6',
  fillOpacity: 0.18,
};

// Leaflet measures its container on mount. Inside a modal or a tab
// that container is still zero-sized at that moment, so the tiles come
// back grey until it is told to re-measure — and the view is
// re-centred whenever a different point is passed in.
const FitToGeofence = ({ center, radius, zoom }) => {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (radius > 0) {
        // toBounds() works off the LatLng itself; Circle.getBounds() would
        // need the circle to already be attached to a map.
        map.fitBounds(L.latLng(center).toBounds(radius * 2), {
          padding: [24, 24],
        });
      } else {
        map.setView(center, zoom);
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [map, center[0], center[1], radius, zoom]);

  return null;
};

FitToGeofence.propTypes = {
  center: PropTypes.arrayOf(PropTypes.number).isRequired,
  radius: PropTypes.number.isRequired,
  zoom: PropTypes.number.isRequired,
};

const GeofenceMap = ({
  lat,
  lng,
  radius,
  zoom,
  markerIconUrl,
  circleStyle,
  scrollWheelZoom,
  emptyText,
  className,
}) => {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  const radiusNum = Number(radius) || 0;
  const hasCoords = Number.isFinite(latNum) && Number.isFinite(lngNum);

  const markerIcon = useMemo(() => {
    if (!markerIconUrl) return undefined;
    return L.icon({
      iconUrl: markerIconUrl,
      iconSize: [38, 38],
      iconAnchor: [19, 38],
      popupAnchor: [0, -38],
    });
  }, [markerIconUrl]);

  const pathOptions = useMemo(
    () => ({ ...DEFAULT_CIRCLE_STYLE, ...circleStyle }),
    [circleStyle]
  );

  if (!hasCoords) {
    return <p className={styles.empty}>{emptyText}</p>;
  }

  const center = [latNum, lngNum];

  return (
    <div className={[styles.frame, className || ''].filter(Boolean).join(' ')}>
      {/* Remounting per point keeps Leaflet from reusing a container
          that was initialised for a different centre. */}
      <MapContainer
        key={`${latNum},${lngNum}`}
        center={center}
        zoom={zoom}
        scrollWheelZoom={scrollWheelZoom}
        className={styles.canvas}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <Marker position={center} icon={markerIcon} />
        {radiusNum > 0 && (
          <Circle center={center} radius={radiusNum} pathOptions={pathOptions} />
        )}
        <FitToGeofence center={center} radius={radiusNum} zoom={zoom} />
      </MapContainer>
    </div>
  );
};

GeofenceMap.propTypes = {
  lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  zoom: PropTypes.number,
  markerIconUrl: PropTypes.string,
  circleStyle: PropTypes.object,
  scrollWheelZoom: PropTypes.bool,
  emptyText: PropTypes.string,
  className: PropTypes.string,
};

GeofenceMap.defaultProps = {
  lat: undefined,
  lng: undefined,
  radius: 0,
  zoom: 16,
  markerIconUrl: '',
  circleStyle: null,
  scrollWheelZoom: true,
  emptyText: 'No coordinates saved, so this cannot be shown on the map.',
  className: '',
};

export default memo(GeofenceMap);
